import React, { useEffect, useRef } from "react";
import { Brand } from "@/components/Layout";
import { GUIDE_PARTS, GUIDE_CHAPTERS, GUIDE_FICHES } from "@/data/guideTdah";
import { Blocks } from "./guideBlocks";
import { partOf } from "./GuideSommaire";

// Monte les pages hors écran, puis appelle onReady(elements) une fois rendues.
export function PdfRunner({ pages, onReady }) {
  const refs = useRef([]);
  useEffect(() => { const t = setTimeout(() => onReady(refs.current.filter(Boolean)), 150); return () => clearTimeout(t); }, [onReady]);
  return (
    <div className="fixed -left-[9999px] top-0" aria-hidden="true" data-testid="guide-pdf-runner">
      {pages.map((node, i) => <div key={i} ref={(el) => { refs.current[i] = el; }} className="bg-white p-10 text-[#4A3B36]" style={{ width: 794, fontFamily: "Georgia, serif" }}>{node}</div>)}
    </div>
  );
}

const Footer = () => <p className="mt-6 text-[9px] text-center text-[#6E5C53]">Guide pratique TDAH — support psychoéducatif à destination des parents et des enseignants. Ne constitue pas un diagnostic.</p>;

const Cover = () => (
  <div className="min-h-[1000px] flex flex-col justify-between">
    <div className="text-center pt-16"><Brand /></div>
    <div className="text-center space-y-6">
      <div className="text-xs uppercase tracking-[0.3em] font-bold" style={{ color: "#B58C67" }}>Guide pratique</div>
      <h1 className="text-5xl font-bold leading-tight">TDAH<br /><span className="text-3xl font-normal">Comprendre, accompagner et soutenir l'enfant au quotidien</span></h1>
      <p className="text-lg text-[#6E5C53]">À destination des parents et des enseignants</p>
      <div className="flex justify-center gap-2 pt-4">{GUIDE_PARTS.map((p) => <span key={p.id} className="px-4 py-1.5 rounded-full text-white text-sm font-bold" style={{ background: p.color }}>{p.title}</span>)}</div>
    </div>
    <div className="pt-10">
      <h2 className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: "#B58C67" }}>Sommaire</h2>
      <ol className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm">{GUIDE_CHAPTERS.map((ch, i) => <li key={ch.id} className="flex gap-2"><span className="w-6 h-6 rounded-full text-white text-[11px] font-bold flex items-center justify-center shrink-0" style={{ background: partOf(ch).color }}>{i + 1}</span>{ch.title}</li>)}</ol>
      <Footer />
    </div>
  </div>
);

const ChapterPage = ({ ch, index }) => {
  const part = partOf(ch);
  return (
    <div className="text-[13.5px] leading-relaxed">
      <div className="flex items-center justify-between border-b-2 pb-2 mb-4" style={{ borderColor: part.color }}>
        <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: part.color }}>{part.title}</span>
        <span className="text-xs text-[#6E5C53]">Chapitre {index + 1} / {GUIDE_CHAPTERS.length}</span>
      </div>
      <h1 className="text-3xl font-bold mb-3">{ch.title}</h1>
      <p className="text-[15px] leading-relaxed mb-6 text-[#6E5C53]">{ch.lead}</p>
      {ch.blocks && <Blocks blocks={ch.blocks} color={part.color} />}
      {ch.cards && ch.cards.map((c) => (
        <div key={c.n} className="mt-4 rounded-2xl border border-[#E5DCD1] p-4">
          <div className="flex items-center gap-3 mb-1"><span className="w-8 h-8 rounded-full text-white font-bold flex items-center justify-center" style={{ background: part.color }}>{c.n}</span><h2 className="text-lg font-bold">{c.title}</h2></div>
          <p className="italic text-[#6E5C53] mb-3">{c.goal}</p>
          <Blocks blocks={c.blocks} color={part.color} />
        </div>
      ))}
    </div>
  );
};

export const guidePages = () => [<Cover key="cover" />, ...GUIDE_CHAPTERS.map((ch, i) => <ChapterPage key={ch.id} ch={ch} index={i} />)];

const FichePage = ({ f }) => (
  <div className="flex flex-col text-[13px]">
    <div className="flex items-center justify-between mb-6"><Brand compact /><span className="text-xs uppercase tracking-[0.25em] font-bold" style={{ color: f.color }}>Fiche réflexe</span></div>
    <h1 className="text-4xl font-bold mb-8 pb-3 border-b-4" style={{ borderColor: f.color }}>{f.title}</h1>
    {f.steps && (
      <ol className="space-y-4">{f.steps.map(([t, d], i) => (
        <li key={t} className="flex gap-4 items-start"><span className="w-12 h-12 rounded-full text-white text-xl font-bold flex items-center justify-center shrink-0" style={{ background: f.color }}>{i + 1}</span><div><div className="text-xl font-bold">{t}</div><div className="text-[15px] text-[#6E5C53] leading-snug">{d}</div></div></li>
      ))}</ol>
    )}
    {f.check && (
      <ul className="grid grid-cols-2 gap-4 text-xl">{f.check.map((c, i) => <li key={i} className="flex items-center gap-3"><span className="w-8 h-8 border-2 rounded shrink-0" style={{ borderColor: f.color }} />{c}</li>)}</ul>
    )}
    {f.levels && (
      <div className="mt-10"><div className="font-bold text-lg mb-3">Vers l'autonomie</div><div className="space-y-2">{f.levels.map((l, i) => <div key={l} className="flex items-center gap-3"><span className="text-xs font-bold w-16">Niveau {i + 1}</span><div className="h-9 rounded-r-full flex items-center px-4 text-white font-semibold" style={{ width: `${40 + (60 * (i + 1)) / f.levels.length}%`, background: f.color, opacity: 0.6 + (0.4 * i) / 3 }}>{l}</div></div>)}</div></div>
    )}
    {f.phrases && (
      <div className="mt-10 rounded-2xl p-5" style={{ background: "#F6EEE4" }}><div className="font-bold mb-2">Les phrases qui aident</div>{f.phrases.map((p) => <div key={p} className="text-[15px] leading-relaxed">{p}</div>)}</div>
    )}
  </div>
);

export const fichePages = (ids) => GUIDE_FICHES.filter((f) => !ids || ids.includes(f.id)).map((f) => <FichePage key={f.id} f={f} />);
